import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery, setAccessToken, setRefreshToken } from '../baseQuery';
import { setAdmin, clearAdmin } from './auth.slice';
import type { AdminAuthResponse } from './entities';

const SESSION_STORAGE_KEY = 'gather.admin.session';
const ADMIN_ROLE = 3;

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery,
  endpoints: (builder) => ({
    login: builder.mutation<
      AdminAuthResponse,
      { email: string; password: string }
    >({
      query: (body) => ({
        url: '/auth/login',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          // Non-admins get a valid response from the backend, but we refuse
          // to persist their session — keeping the admin surface dark for them.
          if (data.user.role !== ADMIN_ROLE) return;
          setAccessToken(data.accessToken);
          setRefreshToken(data.refreshToken);
          try {
            localStorage.setItem(
              SESSION_STORAGE_KEY,
              JSON.stringify({
                accessToken: data.accessToken,
                refreshToken: data.refreshToken,
                user: {
                  id: data.user.id,
                  email: data.user.email,
                  firstName: data.user.firstName,
                  role: data.user.role,
                },
              }),
            );
          } catch {
            // ignore: storage may be unavailable in private modes
          }
          dispatch(setAdmin(data.user));
        } catch {
          // Errors flow through the mutation's error state.
        }
      },
    }),
    logout: builder.mutation<void, void>({
      queryFn: async (_arg, api) => {
        setAccessToken(undefined);
        setRefreshToken(undefined);
        try {
          localStorage.removeItem(SESSION_STORAGE_KEY);
        } catch {
          // ignore
        }
        api.dispatch(clearAdmin());
        return { data: undefined };
      },
    }),
  }),
});

export const { useLoginMutation, useLogoutMutation } = authApi;
